import { callAllHandlers } from "@smartsheet/lodestar-utils";
import { MouseEventHandler, useCallback } from "react";
import { PaginationProps } from "..";

export interface UsePaginationOptions
    extends Omit<
        PaginationProps,
        "from" | "onNextClick" | "onPrevClick" | "onFirstPageClick" | "onLastPageClick" | "onChange"
    > {
    currentPage: number;
    onChange: (newPage: number) => void;
}

export interface UsePaginationResults {
    getPaginationProps: (overrides?: Partial<PaginationProps>) => PaginationProps;
}

export const usePagination = ({
    currentPage,
    onChange,
    total,
    entitiesPerPage,
    ...hookOverrides
}: UsePaginationOptions): UsePaginationResults => {
    const maxPage = Math.ceil(total / entitiesPerPage);

    if (process.env.NODE_ENV !== "production") {
        if (currentPage < 1 || currentPage > maxPage) {
            console.warn(
                `usePagination: currentPage is out of bounds: ${currentPage}. It should be 1 <= currentPage (${currentPage}) <= maxPage (${maxPage}). This will probably mean the pagination component renders incorrectly.`
            );
        }
    }

    const internalNextHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
        const newPage = currentPage + 1;
        if (newPage <= maxPage) {
            onChange(newPage);
        }
    }, [currentPage, maxPage, onChange]);

    const internalPrevHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
        const newPage = currentPage - 1;
        if (newPage >= 1) {
            onChange(newPage);
        }
    }, [onChange, currentPage]);

    const internalFirstPageHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
        if (currentPage !== 1) {
            onChange(1);
        }
    }, [currentPage, onChange]);

    const internalLastPageHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
        if (currentPage !== maxPage) {
            onChange(maxPage);
        }
    }, [currentPage, maxPage, onChange]);

    // In the case where the currentPage is out of the proper range, we'll coerce it here to make
    // sure the Pagination component doesn't throw errors.
    const coercedCurrentPage = Math.max(1, Math.min(currentPage, maxPage));

    return {
        getPaginationProps: useCallback(
            ({
                onFirstPageClick,
                onLastPageClick,
                onNextClick,
                onPrevClick,
                ...propGetterOverrides
            }: Partial<PaginationProps> = {}) => {
                return {
                    ...hookOverrides,
                    ...propGetterOverrides,
                    onFirstPageClick: callAllHandlers(onFirstPageClick, internalFirstPageHandler),
                    onLastPageClick: callAllHandlers(onLastPageClick, internalLastPageHandler),
                    onNextClick: callAllHandlers(onNextClick, internalNextHandler),
                    onPrevClick: callAllHandlers(onPrevClick, internalPrevHandler),
                    from: (coercedCurrentPage - 1) * entitiesPerPage + 1,
                    entitiesPerPage,
                    total,
                };
            },
            [
                coercedCurrentPage,
                entitiesPerPage,
                hookOverrides,
                internalFirstPageHandler,
                internalLastPageHandler,
                internalNextHandler,
                internalPrevHandler,
                total,
            ]
        ),
    };
};
